import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Printer } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { money, fmtDate } from "@/lib/constants";
import { useCamp } from "@/context/SettingsContext";
import PrintLogo from "@/components/print/PrintLogo";
import { Button } from "@/components/ui/button";
import DemoStamp from "@/components/DemoStamp";

const tierName = (t, n) => t(`dunning_tier_name_${n}`);

function DunningDates({ list, t }) {
  if (!list?.length) return <span className="text-slate-400">—</span>;
  return (
    <span className="space-y-0.5">
      {list.map((d, i) => (
        <span key={i} className="block whitespace-nowrap">
          {tierName(t, d.tier)}: <b>{fmtDate(d.at)}</b>
        </span>
      ))}
    </span>
  );
}

export default function PrintDunning() {
  const { t } = useLang();
  const { camp } = useCamp();
  const [params] = useSearchParams();
  const scope = params.get("scope") === "incasso" ? "incasso" : "summary";
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/invoices/dunning-report?scope=${scope}`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [scope]);

  useEffect(() => {
    if (data && !data.error) {
      const timer = setTimeout(() => window.print(), 700);
      return () => clearTimeout(timer);
    }
  }, [data]);

  if (!data) return <div className="p-8 text-sm text-muted-foreground">…</div>;

  const Header = () => (
    <div className="flex items-start justify-between gap-6">
      <PrintLogo camp={camp} testid="print-dunning-logo" />
      <div className="text-right text-[11px] leading-relaxed text-slate-600">
        <p className="font-bold text-sm text-slate-900">{camp.camp_name}</p>
        {[camp.street, camp.postal_city, camp.country, camp.phone, camp.email].filter(Boolean).map((l) => <p key={l}>{l}</p>)}
        {camp.vat_number && <p className="font-semibold text-slate-900">{camp.vat_number}</p>}
      </div>
    </div>
  );

  return (
    <div className="print-page bg-white text-slate-900 mx-auto max-w-[820px] p-10 print:p-0" data-testid="print-dunning">
      <DemoStamp />
      <div className="flex justify-end print:hidden mb-3">
        <Button size="sm" onClick={() => window.print()} data-testid="print-dunning-now">
          <Printer className="h-4 w-4 mr-1" />{t("print")}
        </Button>
      </div>

      {scope === "summary" ? (
        <>
          <Header />
          <h1 className="mt-6 font-display text-2xl font-extrabold">{t("dunning_report_summary_title")}</h1>
          <p className="text-xs text-slate-500 mb-5">{t("dunning_report_generated")}: {fmtDate(data.generated_at)} · {data.count} {t("invoices")}</p>
          {data.rows?.length ? (
            <table className="w-full text-xs" data-testid="dunning-summary-table">
              <thead>
                <tr className="border-b border-slate-300 text-[10px] uppercase tracking-[0.12em] text-slate-500">
                  <th className="text-left py-2">{t("dunning_customer_number")}</th>
                  <th className="text-left py-2">{t("guest")}</th>
                  <th className="text-left py-2">{t("email")}</th>
                  <th className="text-left py-2">{t("invoice")}</th>
                  <th className="text-right py-2">{t("amount_due")}</th>
                  <th className="text-left py-2 pl-3">{t("dunning_label")}</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 align-top" data-testid={`dunning-summary-row-${r.number}`}>
                    <td className="py-2 font-mono">{r.customer_number}</td>
                    <td className="py-2 font-medium">{r.name}{r.company ? ` · ${r.company}` : ""}</td>
                    <td className="py-2">{r.email || "—"}</td>
                    <td className="py-2">{r.number}<span className="block text-slate-400">{fmtDate(r.created_at)}</span></td>
                    <td className="py-2 text-right font-semibold">{money(r.amount_due, r.currency)}</td>
                    <td className="py-2 pl-3"><DunningDates list={r.dunning_sent} t={t} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="text-sm text-slate-500" data-testid="dunning-empty">{t("dunning_none")}</p>}
        </>
      ) : (
        <>
          {data.groups?.length ? data.groups.map((grp, gi) => (
            <div key={gi} className={gi > 0 ? "break-before-page pt-10" : ""} data-testid={`incasso-page-${gi}`}>
              <Header />
              <h1 className="mt-6 font-display text-2xl font-extrabold text-rose-700">{t("dunning_report_incasso_title")}</h1>
              <p className="text-xs text-slate-500 mb-5">{t("dunning_report_generated")}: {fmtDate(data.generated_at)}</p>

              <div className="border border-slate-300 rounded-lg p-4 text-sm">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{t("guest")}</p>
                <p className="font-bold text-base">{grp.guest.name}{grp.guest.company ? ` · ${grp.guest.company}` : ""}</p>
                <div className="mt-1 text-[12px] text-slate-600 grid grid-cols-2 gap-x-6">
                  <span>{t("dunning_customer_number")}: <b className="font-mono">{grp.guest.customer_number}</b></span>
                  <span>{t("email")}: {grp.guest.email || "—"}</span>
                  <span>{t("phone") || "Tel"}: {grp.guest.phone || "—"}</span>
                  <span>{[grp.guest.address, grp.guest.postal_city, grp.guest.country].filter(Boolean).join(", ") || "—"}</span>
                </div>
              </div>

              {grp.reservations?.length > 0 && (
                <div className="mt-5" data-testid="incasso-stays">
                  <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-1">{t("dunning_stays")}</p>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-200 text-[10px] uppercase text-slate-400">
                        <th className="text-left py-1">{t("pitch")}</th>
                        <th className="text-left py-1">{t("arrival")}</th>
                        <th className="text-left py-1">{t("departure")}</th>
                        <th className="text-left py-1">{t("persons") || "Pers."}</th>
                        <th className="text-left py-1">{t("status")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {grp.reservations.map((r, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="py-1">{r.pitch_label || "—"}</td>
                          <td className="py-1">{fmtDate(r.arrival)}</td>
                          <td className="py-1">{fmtDate(r.departure)}</td>
                          <td className="py-1">{r.persons || "—"}</td>
                          <td className="py-1">{r.status || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="mt-5" data-testid="incasso-invoices">
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-1">{t("invoices")}</p>
                {grp.invoices.map((inv) => (
                  <div key={inv.id} className="mb-4 border border-slate-200 rounded-lg p-3">
                    <div className="flex justify-between text-sm font-semibold">
                      <span>{t("invoice")} {inv.number}</span>
                      <span className="text-slate-500">{fmtDate(inv.created_at)}</span>
                    </div>
                    <table className="w-full text-xs mt-2">
                      <tbody>
                        {inv.items.map((it, i) => (
                          <tr key={i} className="border-b border-slate-100">
                            <td className="py-1">{it.description}</td>
                            <td className="py-1 text-right w-12">{it.qty}</td>
                            <td className="py-1 text-right w-24">{money(it.unit_price, inv.currency)}</td>
                            <td className="py-1 text-right w-24 font-medium">{money(it.qty * it.unit_price, inv.currency)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="mt-1 text-right text-xs">
                      <span className="text-slate-500">{t("subtotal")}: {money(inv.subtotal, inv.currency)} · {t("tax")} {inv.tax_rate}%: {money(inv.tax_amount, inv.currency)}</span>
                      <span className="block font-bold text-sm">{t("amount_due")}: {money(inv.amount_due, inv.currency)}</span>
                    </div>
                    <div className="mt-1 text-[11px] text-slate-600">
                      <b>{t("dunning_label")}:</b> <DunningDates list={inv.dunning_sent} t={t} />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 border-t-2 border-rose-300 pt-2 text-right">
                <span className="font-display text-xl font-extrabold text-rose-700">{t("dunning_total_due")}: {money(grp.total_due, "EUR")}</span>
              </div>
            </div>
          )) : <><Header /><p className="mt-6 text-sm text-slate-500" data-testid="incasso-empty">{t("dunning_none")}</p></>}
        </>
      )}
    </div>
  );
}
